/**
 * The pricing rule of ADR-0014, for the settings preview only. The web never prices a real sale:
 * totals come from the API (ADR-0011 clause 4). This twin exists so the owner sees what the rates
 * they type will do, and it must reproduce `packages/api_contracts/fixtures/pricing/pricing_cases.json`
 * exactly, like the Python and Dart versions.
 *
 * BigInt for the intermediate products: `base × 10⁸` passes 2⁵³ for a base above about Rp 90 million.
 */
export type Priced = { service: number; tax: number; rounding: number; net: number }

const halfUp = (n: bigint, d: bigint): bigint => (n + d / 2n) / d

export function priceCheck(
  gross: number,
  discount: number,
  fee: number,
  serviceBps: number,
  taxBps: number,
  inclusive: boolean,
  roundingUnit: 0 | 100,
): Priced {
  const base = BigInt(gross - discount + fee)
  const s = BigInt(serviceBps)
  const t = BigInt(taxBps)
  let service: bigint
  let tax: bigint
  let before: bigint
  if (!inclusive) {
    service = halfUp(base * s, 10_000n)
    tax = halfUp((base + service) * t, 10_000n)
    before = base + service + tax
  } else {
    const pre = halfUp(base * 10_000n * 10_000n, (10_000n + s) * (10_000n + t))
    service = halfUp(pre * s, 10_000n)
    tax = base - pre - service // the remainder, so pre + service + tax == base exactly
    if (tax < 0n) {
      // both roundings went up (tiny amounts): take the overshoot off the service
      service += tax
      tax = 0n
    }
    before = base
  }
  const unit = BigInt(roundingUnit)
  let rounding = 0n
  // A total below half the unit is left alone: rounding never makes a sale Rp 0.
  if (unit > 0n && before >= unit / 2n) rounding = ((before + unit / 2n) / unit) * unit - before
  return { service: Number(service), tax: Number(tax), rounding: Number(rounding), net: Number(before + rounding) }
}
